import { Box, LoadingOverlay, Table } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
} from "@tabler/icons-react";
import type { ReactElement } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../ui/EmptyState";

export interface TableSortProps {
  index: number;
  header?: string | ReactElement;
  direction: "ASC" | "DESC";
}

export interface Props {
  headers: Array<string | ReactElement>;
  rows: React.ReactElement<typeof Table.Tbody>[];
  loading?: boolean;
  allowSorting?: boolean;
  sortableColumns?: number[];
  defaultSort?: TableSortProps;
  onSortChange?: (params: TableSortProps) => void;
}

const BaseTable = ({
  headers,
  rows,
  loading = false,
  allowSorting = false,
  sortableColumns = [],
  defaultSort,
  onSortChange,
}: Props) => {
  const { t } = useTranslation();
  const initialSortColumn = useMemo(
    () => defaultSort?.index ?? null,
    [defaultSort]
  );
  const initialSortDirection = useMemo(
    () => defaultSort?.direction ?? "ASC",
    [defaultSort]
  );

  const [sortColumn, setSortColumn] = useState<number | null>(
    initialSortColumn
  );
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">(
    initialSortDirection
  );

  const handleSortClick = (index: number) => {
    let newDirection: "ASC" | "DESC" = "ASC";

    if (sortColumn === index) {
      newDirection = sortDirection === "ASC" ? "DESC" : "ASC";
    }

    setSortColumn(index);
    setSortDirection(newDirection);

    if (onSortChange) {
      onSortChange({
        index,
        header: headers[index],
        direction: newDirection,
      });
    }
  };

  return (
    <Box pos="relative">
      {rows && rows.length > 0 && (
        <Table
          borderColor="gray.3"
          classNames={{
            table: "bg-white",
            th: "font-bold text-sm",
          }}
          highlightOnHover
          highlightOnHoverColor="rgba(173, 181, 189, 0.1)"
          horizontalSpacing="lg"
          stickyHeader
          verticalSpacing="md"
          withColumnBorders
          withRowBorders
          withTableBorder
          layout="auto"
          style={{ minWidth: '100%' }}
        >
          <Table.Thead>
            <Table.Tr>
              {headers.map((item, index) => {
                const isCheckboxColumn =
                  typeof item === "object" && item.key === "select-all";
                const isSorted = sortColumn === index;
                const isSortable =
                  allowSorting && sortableColumns.includes(index);

                const itemKey =
                  typeof item === "string" ? item : `header-${String(index)}`;

                if (isCheckboxColumn) {
                  return (
                    <Table.Th
                      key={itemKey}
                      className="!bg-client-color/10 backdrop-blur-3xl"
                    >
                      {item}
                    </Table.Th>
                  );
                }

                return (
                  <Table.Th
                    key={itemKey}
                    onClick={
                      isSortable ? () => handleSortClick(index) : undefined
                    }
                    className={`!bg-client-color/10 backdrop-blur-2xl ${
                      isSortable
                        ? "cursor-pointer select-none hover:bg-white-200 transition-colors"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      {item}
                      <div className="text-gray-600">
                        {isSortable &&
                          (isSorted ? (
                            sortDirection === "ASC" ? (
                              <IconArrowUp size={14} />
                            ) : (
                              <IconArrowDown size={14} />
                            )
                          ) : (
                            <IconArrowsSort size={14} />
                          ))}
                      </div>
                    </div>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      )}

      {(!rows || rows.length <= 0) && (
        <div className="h-96 w-full">
          <EmptyState title={t("common.noData")} />
        </div>
      )}

      <LoadingOverlay
        classNames={{
          overlay: "bg-white/20",
        }}
        overlayProps={{ radius: "sm", blur: 3 }}
        visible={loading}
        zIndex={20}
      />
    </Box>
  );
};

export default BaseTable;
